import { useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { Alert, AuthLayout, Button, Field } from '@/components/ui';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';

/** Etapa 2 do acesso: cadastro do 2FA (primeira vez) ou digitação do código. */
export function MfaPage({ mode }: { mode: 'setup' | 'verify' }) {
  return mode === 'setup' ? <MfaSetup /> : <MfaVerify />;
}

function MfaSetup() {
  const { refresh, logout } = useAuth();
  const [qr, setQr] = useState<{ qrCode: string; chaveManual: string } | null>(null);
  const [codigo, setCodigo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [recovery, setRecovery] = useState<string[] | null>(null);

  useEffect(() => {
    api
      .post<{ qrCode: string; chaveManual: string }>('/auth/mfa/setup')
      .then(setQr)
      .catch((err: unknown) => setError(errorMessage(err)));
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await api.post<{ codigosRecuperacao: string[] }>('/auth/mfa/activate', { codigo });
      setRecovery(res.codigosRecuperacao);
    } catch (err) {
      setError(errorMessage(err));
      setCodigo('');
    } finally {
      setLoading(false);
    }
  }

  if (recovery) return <RecoveryCodes codes={recovery} onDone={() => void refresh()} />;

  return (
    <AuthLayout
      step="PASSO 2 DE 2"
      title="Ative a verificação em duas etapas"
      subtitle="Ela protege sua conta mesmo que alguém descubra sua senha."
    >
      <ol className="steps">
        <li>
          Instale no celular o <strong>Google Authenticator</strong> ou o <strong>Microsoft Authenticator</strong>.
        </li>
        <li>No aplicativo, toque em “+” e escaneie o QR code abaixo.</li>
        <li>Digite o código de 6 números que aparecer.</li>
      </ol>

      {qr ? (
        <div className="qr">
          <img src={qr.qrCode} alt="QR code para cadastrar o sistema no aplicativo autenticador" width={200} height={200} />
          <details>
            <summary>Não consigo escanear</summary>
            <p className="small">
              No aplicativo, escolha “inserir chave manualmente” e digite: <code className="secret">{qr.chaveManual}</code>
            </p>
          </details>
        </div>
      ) : (
        !error && <p className="muted">Gerando QR code…</p>
      )}

      <form className="stack" onSubmit={(e) => void submit(e)}>
        {error && <Alert>{error}</Alert>}
        <Field
          label="Código de 6 números"
          value={codigo}
          onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          className="otp"
          required
        />
        <Button type="submit" loading={loading} disabled={codigo.length !== 6}>
          Ativar e entrar
        </Button>
        <Button type="button" variant="ghost" onClick={() => void logout()}>
          Cancelar
        </Button>
      </form>
    </AuthLayout>
  );
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <AuthLayout
      title="Guarde seus códigos de recuperação"
      subtitle="Se você perder ou trocar de celular, cada código abaixo permite entrar uma única vez. Eles não serão mostrados de novo."
    >
      <ul className="codes" aria-label="Códigos de recuperação">
        {codes.map((c) => (
          <li key={c}>
            <code>{c}</code>
          </li>
        ))}
      </ul>
      <div className="row">
        <Button type="button" variant="secondary" onClick={() => void copy()}>
          {copied ? 'Copiado!' : 'Copiar códigos'}
        </Button>
        <Button type="button" variant="secondary" onClick={() => window.print()}>
          Imprimir
        </Button>
      </div>
      <Alert kind="warning">Guarde em local seguro, fora do celular (por exemplo, impresso ou num gerenciador de senhas).</Alert>
      <label className="check">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
        Guardei os códigos em local seguro
      </label>
      <Button type="button" disabled={!saved} onClick={onDone}>
        Continuar
      </Button>
    </AuthLayout>
  );
}

function MfaVerify() {
  const { refresh, logout } = useAuth();
  const [useRecovery, setUseRecovery] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await api.post('/auth/mfa/verify', useRecovery ? { codigoRecuperacao: codigo } : { codigo });
      await refresh();
    } catch (err) {
      setError(errorMessage(err));
      setCodigo('');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      step="PASSO 2 DE 2"
      title="Código de verificação"
      subtitle={
        useRecovery
          ? 'Digite um dos códigos de recuperação que você guardou. Cada um vale uma única vez.'
          : 'Abra o aplicativo autenticador no celular e digite o código de 6 números.'
      }
    >
      <form className="stack" onSubmit={(e) => void submit(e)}>
        {error && <Alert>{error}</Alert>}
        {useRecovery ? (
          <Field
            label="Código de recuperação"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
            autoComplete="off"
            placeholder="XXXX-XXXX"
            autoFocus
            required
          />
        ) : (
          <Field
            label="Código de 6 números"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            className="otp"
            autoFocus
            required
          />
        )}
        <Button type="submit" loading={loading} disabled={!useRecovery && codigo.length !== 6}>
          Entrar no sistema
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setUseRecovery(!useRecovery);
            setCodigo('');
            setError(null);
          }}
        >
          {useRecovery ? 'Usar o código do aplicativo' : 'Perdi o celular: usar código de recuperação'}
        </Button>
        <Button type="button" variant="ghost" onClick={() => void logout()}>
          Voltar ao login
        </Button>
      </form>
    </AuthLayout>
  );
}
